import axios, { type AxiosInstance } from "axios"
import { getExhibitionHeader } from "../utils/exhibitionContext";
import type { ExhibitorCar, ExhibitorOption, ParticipationOption, CanCreateResponse } 
from "../Component/Application/StaffCars/types"

const api: AxiosInstance = axios.create({
  baseURL: process.env.REACT_APP_API_URL || "http://127.0.0.1:8000",
  headers: {
    "Content-Type": "application/json",
  },
})

api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("accessToken")
    if (token) {
      config.headers["Authorization"] = `Bearer ${token}`
    Object.assign(config.headers, getExhibitionHeader());
    }
    return config
  },
  (error) => Promise.reject(error),
)

interface PaginatedResponse<T> {
  count: number
  next: number | null
  previous: number | null
  total_pages: number
  current_page: number
  page_size: number
  results: T[]
}

interface CarStats {
  total_cars: number
  active_cars: number
  inactive_cars: number
  staff_cars: number
  admin_cars: number
}

export const ExhibitorCarService = {

    getAll: async (page?: number, pageSize?: number, search?: string): Promise<PaginatedResponse<ExhibitorCar>> => {
    const params = new URLSearchParams()

    if (page) params.append("page", page.toString())
    if (pageSize) params.append("page_size", pageSize.toString())
    if (search) params.append("search", search)

    const queryString = params.toString()
    const url = queryString ? `/api/exhibitorsaccess/exhibitor-cars/?${queryString}` : "/api/exhibitor-cars/"

    const response = await api.get(url)
    return response.data
  },

  getWhitelist: async (page?: number, pageSize?: number, search?: string): Promise<PaginatedResponse<ExhibitorCar>> => {
    const params = new URLSearchParams()

    if (page) params.append("page", page.toString())
    if (pageSize) params.append("page_size", pageSize.toString())
    if (search) params.append("search", search)

    const queryString = params.toString()
    const url = queryString ? `/api/exhibitorsaccess/exhibitor-cars/whitelist/?${queryString}` : "/api/exhibitor-cars/whitelist/"

    const response = await api.get(url)
    return response.data
  },
  

  getById: async (id: number): Promise<ExhibitorCar> => {
    const response = await api.get(`/api/exhibitorsaccess/exhibitor-cars/${id}/`)
    return response.data
  },


  getMyCars: async (): Promise<ExhibitorCar[]> => {
    const response = await api.get("/api/exhibitorsaccess/exhibitor-cars/my-cars/")
    return response.data
  },

  canCreate: async (): Promise<CanCreateResponse> => {
    const response = await api.get("/api/exhibitorsaccess/exhibitor-cars/can-create/")
    return response.data
  },

  getExhibitors: async (): Promise<ExhibitorOption[]> => {
    const response = await api.get("/api/exhibitorsaccess/exhibitor-cars/exhibitors/")
    return response.data
  },

  getParticipations: async (exhibitorId: number): Promise<ParticipationOption[]> => {
    const response = await api.get(`/api/exhibitorsaccess/exhibitor-cars/participations/?exhibitor_id=${exhibitorId}`)
    return response.data
  },

  getStats: async (): Promise<CarStats> => {
    const response = await api.get("/api/exhibitorsaccess/exhibitor-cars/stats/")
    return response.data
  },

  create: async (data: Omit<ExhibitorCar, "id" | "created_at" | "created_by">): Promise<ExhibitorCar> => {
    const response = await api.post("/api/exhibitorsaccess/exhibitor-cars/create/", data)
    return response.data
  },

  update: async (id: number, data: Omit<ExhibitorCar, "id" | "created_at" | "created_by">): Promise<ExhibitorCar> => {
    const response = await api.put(`/api/exhibitorsaccess/exhibitor-cars/${id}/update/`, data)
    return response.data
  },

  delete: async (id: number): Promise<void> => {
    await api.delete(`/api/exhibitorsaccess/exhibitor-cars/${id}/delete/`)
  },
}
